import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { createOrganization, signIn, signOut, signUp, type SessionUser } from '../lib/auth'

function Brand() {
  return (
    <div className="flex items-center gap-2.5 pb-1">
      <span className="flex size-7 items-center justify-center rounded-md border border-edge bg-ink-800">
        <span className="size-2 rounded-full bg-sync" />
      </span>
      <div>
        <p className="mono text-sm font-semibold tracking-[0.28em] text-zinc-100">DATUM</p>
        <p className="text-[10px] tracking-wide text-zinc-500">geolocated field notes · offline-first</p>
      </div>
    </div>
  )
}

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-950 px-4">
      <div className="w-full max-w-sm rounded-xl border border-edge bg-ink-900 p-6 shadow-2xl shadow-black/60">
        {children}
      </div>
    </div>
  )
}

function FormError({ message }: { message: string }) {
  return <p className="mono mt-3 rounded border border-conflict/30 bg-conflict/10 px-2 py-1.5 text-[11px] text-conflict">{message}</p>
}

const inputClass =
  'mt-1 w-full rounded border border-edge bg-ink-850 px-3 py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-edge-strong'

/** Email/password gate; Better Auth owns the session cookie, we only bootstrap the UI. */
export function AuthGate() {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'sign-in' | 'sign-up'>('sign-in')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Landing target of the verification email callback (?verified=1).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('verified') === '1') {
      setNotice('Email verified — sign in to continue.')
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      let result
      if (mode === 'sign-up') {
        if (name.trim().length < 2) throw new Error('Name is required')
        if (password.length < 8) throw new Error('Password must be at least 8 characters')
        result = await signUp(name.trim(), email.trim(), password)
      } else {
        result = await signIn(email.trim(), password)
      }
      if (!result.signedIn) {
        setNotice('Almost there — a verification link was emailed to you. Confirm it, then sign in.')
      }
      await queryClient.invalidateQueries({ queryKey: ['session'] })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Authentication failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <CardShell>
      <Brand />
      <div className="mono mb-4 mt-4 flex gap-1 text-[11px]">
        {(['sign-in', 'sign-up'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            className={`rounded px-2.5 py-1 uppercase tracking-[0.1em] ${
              mode === value ? 'bg-ink-800 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {value === 'sign-in' ? 'Sign in' : 'Create account'}
          </button>
        ))}
      </div>
      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-3">
        {mode === 'sign-up' && (
          <label className="block text-[11px] uppercase tracking-wide text-zinc-500">
            Name
            <input
              className={inputClass}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Curtis Alvarez"
              autoComplete="name"
            />
          </label>
        )}
        <label className="block text-[11px] uppercase tracking-wide text-zinc-500">
          Email
          <input
            className={inputClass}
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@firm.example"
            autoComplete="email"
          />
        </label>
        <label className="block text-[11px] uppercase tracking-wide text-zinc-500">
          Password
          <input
            className={inputClass}
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="min 8 characters"
            autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="mt-1 rounded bg-zinc-100 py-2 text-sm font-semibold text-zinc-950 hover:bg-white disabled:opacity-50"
        >
          {busy ? 'Working…' : mode === 'sign-up' ? 'Create account' : 'Sign in'}
        </button>
      </form>
      {error !== null && <FormError message={error} />}
      {notice !== null && (
        <p className="mono mt-3 rounded border border-sync/30 bg-sync/10 px-2 py-1.5 text-[11px] text-sync">{notice}</p>
      )}
      <p className="mono mt-4 text-[10px] leading-relaxed text-zinc-600">
        New accounts get a starter organization on the next screen. Notes are queued locally until the
        API is reachable.
      </p>
      <p className="mono mt-2 text-[10px] text-zinc-700">
        <a href="/privacy" className="hover:text-zinc-400">privacy</a>
        {'  ·  '}
        <a href="/terms" className="hover:text-zinc-400">terms</a>
      </p>
    </CardShell>
  )
}

function suggestSlug(name: string): string {
  const base = name
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')
    .slice(0, 20)
  const suffix = Array.from(crypto.getRandomValues(new Uint8Array(3)))
    .map((byte) => byte.toString(36).slice(0, 2))
    .join('')
    .slice(0, 4)
  return `${base.length > 0 ? base : 'datum'}-${suffix}`
}

/** First-run tenant bootstrap: organization endpoints come from Better Auth's plugin. */
export function CreateOrgCard({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient()
  const [orgName, setOrgName] = useState('Field Operations')
  const [slug, setSlug] = useState(() => suggestSlug('field operations'))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function create() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await createOrganization(orgName.trim() || 'Field Operations', slug.trim() || suggestSlug(orgName))
      await queryClient.invalidateQueries({ queryKey: ['orgs'] })
      await queryClient.invalidateQueries({ queryKey: ['points'] })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the organization')
    } finally {
      setBusy(false)
    }
  }

  return (
    <CardShell>
      <Brand />
      <p className="mt-4 text-[13px] text-zinc-400">
        Signed in as <span className="mono text-zinc-200">{user.email}</span>. Datum scopes notes to an
        organization so crews on the same site share waypoints.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void create()
        }}
        className="mt-4 flex flex-col gap-3"
      >
        <label className="block text-[11px] uppercase tracking-wide text-zinc-500">
          Organization
          <input className={inputClass} value={orgName} onChange={(event) => setOrgName(event.target.value)} />
        </label>
        <label className="block text-[11px] uppercase tracking-wide text-zinc-500">
          Slug
          <input
            className={`${inputClass} mono`}
            value={slug}
            onChange={(event) => setSlug(event.target.value.toLowerCase())}
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-zinc-100 py-2 text-sm font-semibold text-zinc-950 hover:bg-white disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create organization'}
        </button>
      </form>
      {error !== null && <FormError message={error} />}
      <button
        type="button"
        onClick={() => void signOut().then(() => queryClient.invalidateQueries({ queryKey: ['session'] }))}
        className="mono mt-4 self-center text-[10px] text-zinc-600 hover:text-zinc-400"
      >
        sign out
      </button>
    </CardShell>
  )
}
